import React, { useState, useEffect, useRef } from 'react';
import '../Css/AdminUser.css';
import { API_URL } from '../utils/Function';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faPlus,
  faEdit,
  faTrash,
  faSave,
  faTimes,
  faInfoCircle,
  faEnvelope,
  faLock,
  faUserShield,
  faUser,
  faCamera,
  faEye,
  faEyeSlash,
  faCalendar,
  faExclamationTriangle,
  faUserCircle,
} from '@fortawesome/free-solid-svg-icons';

export default function AdminUser() {
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    image: null
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/user.php/get`);
      const data = await response.json();
      if (data.success) {
        setUsers(data.users || []);
      } else {
        toast.error(data.error || 'Error fetching users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Error fetching users');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditMode(true);
      setSelectedUser(user);
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '', // Don't pre-fill password for security
        role: user.role || 'user',
        image: null
      });
      
      // Set image preview if user has an image
      if (user.image_url) {
        setImagePreview(user.image_url);
      } else {
        setImagePreview(null);
      }
    } else {
      setEditMode(false);
      setSelectedUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'user',
        image: null
      });
      setImagePreview(null);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setSelectedUser(null);
    setImagePreview(null);
    setShowPassword(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
    } else if (type === 'file') {
      if (name === 'image') {
        setFormData({ ...formData, [name]: files[0] });
        
        // Create preview for image
        if (files[0]) {
          const reader = new FileReader();
          reader.onload = (e) => {
            setImagePreview(e.target.result);
          };
          reader.readAsDataURL(files[0]);
        } else {
          setImagePreview(null);
        }
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const removeImage = () => {
    setFormData({ ...formData, image: null });
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  
  try {
    if (editMode) {
      await updateUser();
    } else {
      await createUser();
    }
  } catch (error) {
    console.error('Error submitting form:', error);
    toast.error('An error occurred while submitting the form', { className: 'toast-error' });
  }
};

const createUser = async () => {
  const formDataToSend = new FormData();
  
  // Append all form data for new user
  Object.keys(formData).forEach(key => {
    if (formData[key] !== null && formData[key] !== '') {
      formDataToSend.append(key, formData[key]);
    }
  });

  if (formData.image) {
    formDataToSend.append('image', formData.image);
  }

  const response = await fetch(`${API_URL}/user.php/post`, {
    method: 'POST',
    body: formDataToSend
  });

  const data = await response.json();
  
  if (data.success) {
    fetchUsers();
    handleCloseModal();
    toast.success('User added successfully!', { className: 'toast-add' });
  } else {
    throw new Error(data.error || 'An error occurred');
  }
};

const updateUser = async () => {
  // Use PUT method as your PHP expects
  const formDataToSend = new FormData();
  
  formDataToSend.append('name', formData.name);
  formDataToSend.append('email', formData.email);
  formDataToSend.append('role', formData.role);
  
  if (formData.password && formData.password !== '') {
    formDataToSend.append('password', formData.password);
  }
  
  if (formData.image) {
    formDataToSend.append('image', formData.image);
  }

  try {
    const response = await fetch(`${API_URL}/user.php/put/${selectedUser.id}`, {
      method: 'PUT', // Use PUT method as your PHP expects
      body: formDataToSend
    });

    const textResponse = await response.text();
    
    let data;
    try {
      data = JSON.parse(textResponse);
    } catch (parseError) {
      console.error('JSON parse error:', parseError);
      throw new Error('Invalid response from server');
    }

    if (data.success) {
      fetchUsers();
      handleCloseModal();
      toast.success('User updated successfully!', { className: 'toast-edit' });
    } else {
      throw new Error(data.error || 'Update failed');
    }
  } catch (error) {
    console.error('Fetch error:', error);
    throw error;
  }
};

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await fetch(`${API_URL}/user.php/delete/${deleteId}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      if (data.success) {
        fetchUsers();
        setShowDeleteModal(false);
        setDeleteId(null);
        toast.warning('User deleted successfully!', { className: 'toast-delete' });
      } else {
        toast.error(data.error || 'An error occurred', { className: 'toast-error' });
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('An error occurred while deleting the user', { className: 'toast-error' });
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'admin':
        return 'role-badge-admin';
      case 'user':
        return 'role-badge-user';
      case 'moderator':
        return 'role-badge-moderator';
      default:
        return 'role-badge-user';
    }
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'admin':
        return 'Administrator';
      case 'user':
        return 'User';
      case 'moderator':
        return 'Moderator';
      default:
        return role;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="admin-user-container">
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      
      <div className="admin-user-header">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6">
              <h1 className="admin-user-heading">
                <FontAwesomeIcon icon={faUsers} className="me-3" />
                User Management
              </h1>
            </div>
            <div className="col-md-6 text-end">
              <button className="add-user-btn" onClick={() => handleOpenModal()}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add New User
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mt-5">
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : users.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <FontAwesomeIcon icon={faUserCircle} />
            </div>
            <p className="empty-state-text">No users found. Create your first user!</p>
          </div>
        ) : (
          <div className="row">
            {users.map((user) => (
              <div key={user.id} className="col-lg-4 col-md-6 mb-4">
                <div className="user-item-card">
                  {/* User Image Circle */}
                  <div className="user-image-container">
                    <div className="user-image-circle">
                      {user.image_url ? (
                        <img 
                          src={`${API_URL}/${user.image_url}`} 
                          alt={user.name}
                          className="user-profile-image"
                        />
                      ) : (
                        <div className="user-image-placeholder">
                          <FontAwesomeIcon icon={faUser} />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* User Info */}
                  <div className="user-item-body">
                    <h5 className="user-item-name">{user.name}</h5>
                    <p className="user-item-email">
                      <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                      {user.email}
                    </p>
                    
                    <div className="user-meta-info">
                      <div className="user-meta-item">
                        <FontAwesomeIcon icon={faUserShield} className="me-2" />
                        <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                          {getRoleDisplayName(user.role)}
                        </span>
                      </div>
                      
                      {user.created_at && (
                        <div className="user-meta-item">
                          <FontAwesomeIcon icon={faCalendar} className="me-2" />
                          <span>Joined: {formatDate(user.created_at)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="user-item-footer">
                    <button 
                      className="btn btn-sm btn-outline-primary" 
                      onClick={() => handleOpenModal(user)}
                      title="Edit User"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-danger" 
                      onClick={() => handleDeleteClick(user.id)}
                      title="Delete User"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* User Modal */}
      {showModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FontAwesomeIcon icon={editMode ? faEdit : faPlus} className="me-2" />
                  {editMode ? 'Edit User' : 'Add New User'}
                </h5>
                <button type="button" className="btn-close" onClick={handleCloseModal}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit} id="userForm">
                  <div className="row">
                    {/* Profile Image Section */}
                    <div className="col-12 mb-4 text-center">
                      <div className="user-modal-image-container">
                        <div className="user-modal-image-circle">
                          {imagePreview ? (
                            <img 
                              src={`${API_URL}/${imagePreview}`} 
                              alt="Profile preview"
                              className="user-modal-profile-image"
                            />
                          ) : (
                            <div className="user-modal-image-placeholder">
                              <FontAwesomeIcon icon={faUser} />
                            </div>
                          )}
                        </div>
                        
                        <div className="image-upload-controls mt-3">
                          <label htmlFor="imageUpload" className="btn btn-outline-primary btn-sm">
                            <FontAwesomeIcon icon={faCamera} className="me-2" />
                            {imagePreview ? 'Change Image' : 'Upload Image'}
                          </label>
                          <input 
                            type="file" 
                            id="imageUpload"
                            className="d-none" 
                            name="image" 
                            onChange={handleInputChange} 
                            accept="image/*"
                          />
                          
                          {imagePreview && (
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm ms-2"
                              onClick={removeImage}
                            >
                              <FontAwesomeIcon icon={faTimes} className="me-1" />
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Basic Information Section */}
                    <div className="col-12 mb-4">
                      <h6 className="text-primary fw-bold mb-3">
                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                        Basic Information
                      </h6>
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faUser} className="me-1" />
                        Full Name <span className="text-danger">*</span>
                      </label>
                      <input 
                        type="text" 
                        className="form-control" 
                        name="name" 
                        value={formData.name} 
                        onChange={handleInputChange} 
                        placeholder="Enter user's full name"
                        required 
                      />
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                        Email Address <span className="text-danger">*</span>
                      </label>
                      <input 
                        type="email" 
                        className="form-control" 
                        name="email" 
                        value={formData.email} 
                        onChange={handleInputChange} 
                        placeholder="user@example.com"
                        required 
                      />
                    </div>

                    {/* Password Section */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faLock} className="me-1" />
                        Password {!editMode && <span className="text-danger">*</span>}
                      </label>
                      <div className="password-input-group">
                        <input 
                          type={showPassword ? "text" : "password"} 
                          className="form-control" 
                          name="password" 
                          value={formData.password} 
                          onChange={handleInputChange} 
                          placeholder={editMode ? "Leave blank to keep current password" : "Enter password"}
                          required={!editMode}
                        />
                        <button
                          type="button"
                          className="password-toggle-btn"
                          onClick={togglePasswordVisibility}
                        >
                          <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                        </button>
                      </div>
                      {editMode && (
                        <small className="text-muted">
                          Leave password blank to keep the current password
                        </small>
                      )}
                    </div>

                    {/* Role Section */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faUserShield} className="me-1" />
                        User Role <span className="text-danger">*</span>
                      </label>
                      <select 
                        className="form-select" 
                        name="role" 
                        value={formData.role} 
                        onChange={handleInputChange}
                        required
                      >
                        <option value="user">User</option>
                        {/* <option value="moderator">Moderator</option> */}
                        <option value="admin">Administrator</option>
                      </select>
                    </div>

                    {/* Additional Information (for future expansion) */}
                    {editMode && selectedUser && (
                      <>
                        <div className="col-12 mb-4 mt-3">
                          <h6 className="text-primary fw-bold mb-3">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                            Additional Information
                          </h6>
                        </div>
                        
                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            <FontAwesomeIcon icon={faCalendar} className="me-1" />
                            Created At
                          </label>
                          <input 
                            type="text" 
                            className="form-control" 
                            value={formatDate(selectedUser.created_at)} 
                            disabled 
                          />
                        </div>
                        
                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            <FontAwesomeIcon icon={faCalendar} className="me-1" />
                            Last Updated
                          </label>
                          <input 
                            type="text" 
                            className="form-control" 
                            value={formatDate(selectedUser.updated_at)} 
                            disabled 
                          />
                        </div>
                      </>
                    )}
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  <FontAwesomeIcon icon={faTimes} className="me-1" />
                  Cancel
                </button>
                <button type="submit" form="userForm" className="btn btn-primary">
                  <FontAwesomeIcon icon={editMode ? faSave : faPlus} className="me-1" />
                  {editMode ? 'Update User' : 'Add User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-danger">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                  Confirm Delete
                </h5>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this user? This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  <FontAwesomeIcon icon={faTimes} className="me-1" />
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleConfirmDelete}>
                  <FontAwesomeIcon icon={faTrash} className="me-1" />
                  Delete User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}